import { fmtDate, kr, nf0, num } from '../calc/format.js';
import { WEEKDAYS } from '../calc/model.js';
import { hourSpan } from '../calc/heatmap.js';
import { el } from './dom.js';
import { state } from './state.js';

/* ---- Diagram ---- */
export const CHART_BASE = {
  responsive:true, maintainAspectRatio:false,
  // Med minskad rörelse i systemet ritas diagrammen direkt
  animation: typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches
    ? false : {duration:220, easing:'easeOutQuart'},
  plugins:{
    // usePointStyle ritar linjedataset som en tom ring, vilket är svårt att
    // koppla ihop med linjen i diagrammet. Standardrutan bär färgen tydligare.
    legend:{labels:{font:{family:"'Roboto'",size:12}, color:'#56657B',
                    boxWidth:14, boxHeight:10}},
    tooltip:{
      backgroundColor:'#2F435D', padding:10, cornerRadius:4,
      titleFont:{family:"'Roboto Slab'",size:13}, bodyFont:{family:"'Roboto'",size:13},
      callbacks:{ label: c => `${c.dataset.label}: ${c.dataset.isCount ? num(c.parsed.y) : kr(c.parsed.y)}` }
    }
  },
  scales:{
    x:{grid:{display:false},
       ticks:{font:{family:"'Roboto'",size:11}, color:'#56657B',
              // Med många dagar i perioden skulle etiketterna trycka ihop sig
              // till en oläslig rad. Chart.js hoppar över mellanliggande.
              autoSkip:true, maxRotation:0, minRotation:0, maxTicksLimit:14}},
    y:{beginAtZero:true, grid:{color:'#E1E7EE'}, border:{display:false},
       ticks:{font:{family:"'Roboto'",size:11}, color:'#56657B',
              maxTicksLimit:6,
              callback:v => nf0.format(v)}}
  }
};

export function destroyChart(k){
  if(state.charts[k]){ state.charts[k].destroy(); state.charts[k] = null; }
}

export function renderDailyChart(s, has){
  destroyChart('daily');
  const ctx = el('cDaily');
  if(!has){
    el('dailySub').textContent = 'Ingen försäljning i perioden';
    return;
  }

  const days = [...s.byDay.keys()].sort();
  const rev  = days.map(d => s.byDay.get(d).revenue);

  // Ett 7-dagars snitt kräver minst en dryg vecka för att betyda något.
  // Underrubriken sätts efter vad som faktiskt ritas, så att den aldrig
  // lovar en linje som uteblir.
  const showAvg = days.length >= 7;
  el('dailySub').textContent = showAvg
    ? 'Staplarna är dagens omsättning, linjen ett snitt över 7 dagar med försäljning'
    : (days.length === 1
        ? 'Omsättningen under dagen'
        : `Omsättning per dag, ${days.length} dagar i perioden`);

  const avg = rev.map((_,i) => {
    const from = Math.max(0, i-6);
    const slice = rev.slice(from, i+1);
    return slice.reduce((a,b)=>a+b,0) / slice.length;
  });

  const ds = [{
    type:'bar', label:'Omsättning', data:rev,
    backgroundColor:'#00A8E2', borderRadius:2, maxBarThickness:38
  }];
  if(showAvg){
    ds.push({type:'line', label:'Snitt över 7 dagar med försäljning', data:avg,
      borderColor:'#2F435D', borderWidth:2, pointRadius:0, tension:.35, fill:false});
  }

  state.charts.daily = new Chart(ctx, {
    data:{labels: days.map(d => fmtDate(d).replace(/ \d{4}$/,'')), datasets:ds},
    options: CHART_BASE
  });
}

export function renderWeekdayChart(s, has){
  destroyChart('weekday');
  if(!has) return;
  // Veckodagar utan försäljning utelämnas, precis som i tabellen nedanför.
  // En tom plats i diagrammet ser ut som saknad data i stället för stängt.
  const idx = WEEKDAYS.map((_,i) => i).filter(i => (s.byWeekday.get(i)?.tx || 0) > 0);
  if(!idx.length) return;
  state.charts.weekday = new Chart(el('cWeekday'), {
    type:'bar',
    data:{labels: idx.map(i => WEEKDAYS[i]),
          datasets:[{label:'Omsättning', data: idx.map(i => s.byWeekday.get(i).revenue),
                     backgroundColor:'#00A8E2', borderRadius:2, maxBarThickness:52}]},
    options: {...CHART_BASE, plugins:{...CHART_BASE.plugins, legend:{display:false}}}
  });
}

/**
 * Timmarna som visas i tidsdiagrammen och värmekartan. En lucka mitt på dagen
 * — en lunchstängning, en lugn timme — fylls ut så att dagen syns i ett
 * stycke. Ett längre glapp gör det inte: ett enda kvitto strax efter midnatt
 * skulle annars dra ut axeln över hela natten, med nio tomma timmar.
 */
export function activeHours(s){
  return hourSpan(s.byHour.keys());
}

export function renderHourCharts(s, has){
  destroyChart('hourRev'); destroyChart('hourTx');
  if(!has) return;

  const hrs = activeHours(s);
  const labels = hrs.map(h => String(h).padStart(2,'0') + ':00');

  state.charts.hourRev = new Chart(el('cHourRev'), {
    type:'bar',
    data:{labels, datasets:[{label:'Omsättning', data:hrs.map(h => s.byHour.get(h)||0),
      backgroundColor:'#00A8E2', borderRadius:2}]},
    options:{...CHART_BASE, plugins:{...CHART_BASE.plugins, legend:{display:false}}}
  });

  state.charts.hourTx = new Chart(el('cHourTx'), {
    type:'bar',
    data:{labels, datasets:[{label:'Antal köp', isCount:true, data:hrs.map(h => s.byHourTx.get(h)||0),
      backgroundColor:'#2F435D', borderRadius:2}]},
    options:{...CHART_BASE, plugins:{...CHART_BASE.plugins, legend:{display:false}},
      scales:{...CHART_BASE.scales, y:{...CHART_BASE.scales.y, ticks:{...CHART_BASE.scales.y.ticks, precision:0}}}}
  });
}
